<template>
    <Card :title="task.name" :isShowError="isFailed" :refresh="refreshCard" :disableRetry="disableRetry">
        <template #icon>
            <PtIcon icon="TechBg" class="text-[40px]" />
        </template>
        <div v-if="isLoading" class="ml-[50%] mt-[60px] flex -translate-x-1/2 flex-col items-center">
            <span class="th-loading th-loading-spinner th-loading-lg"></span>
            <div class="mt-4 text-sm text-black">{{ t('report.creating') }}</div>
        </div>
        <template v-else>
            <ParagraphWithTitle :content="cardInfo?.description" class="mb-5">
                <div class="mt-4" v-if="cardInfo?.rag_list && cardInfo?.rag_list.length > 0">
                    <button
                        class="th-btn th-btn-text th-btn-sm"
                        @click="handleViewSources(task)"
                    >
                        <PtIcon class="th-btn-icon text-xl" icon="SolidChain" />
                        <span>{{ t('report.viewSource') }}</span>
                    </button>
                </div>
            </ParagraphWithTitle>
            <div class="grid grid-cols-1 grid-rows-2 gap-5 rounded bg-gray-10 p-4 lg:grid-cols-2 lg:grid-rows-1"
                 v-if="cardInfo?.patentChartData || cardInfo?.paperChartData">
                <div v-if="cardInfo?.patentChartData">
                    <p class="mb-2 text-sm font-semibold leading-5 text-gray-450">{{ t('report.patentTrend') }}</p>
                    <BIChart ref="patentTrend" :chartKey="charts[0].chartType" :isLoading="isLoading"
                             :configTaskFunc="configTaskFunc(charts[0].config)" :colorFunc="colorFunc(charts[0].chartType)"
                             :dataTaskFunc="async () => cardInfo?.patentChartData" @click="handlePatentClick"
                             @chart-loaded="isPatentChartLoaded = true"
                             class="flex h-[188px] w-full items-center justify-center bg-white" />
                </div>
                <div v-if="cardInfo?.paperChartData">
                    <p class="mb-2 text-sm font-semibold leading-5 text-gray-450">{{ t('report.paperTrend') }}</p>
                    <BIChart ref="paperTrend" :chartKey="charts[1].chartType" :isLoading="isLoading"
                             :configTaskFunc="configTaskFunc(charts[1].config)" :colorFunc="colorFunc(charts[1].chartType)"
                             :dataTaskFunc="async () => cardInfo?.paperChartData" @click="handlePaperClick"
                             @chart-loaded="isPaperChartLoaded = true"
                             class="flex h-[188px] w-full items-center justify-center bg-white" />
                </div>
            </div>
        </template>
    </Card>
</template>

<script setup lang="ts">
import { PtIcon } from 'patsnap-biz'
import { watch, ref, computed } from 'vue';
import Card from '@/components/report/Card.vue';
import ParagraphWithTitle from '@/components/report/ParagraphWithTitle.vue';
import BIChart from '@/components/analysis/BIChart.vue';
import { useTechBgObjectives } from '@/composables/use-tech-bg-objectives';
import { useGlobalState } from '@/composables/use-global-store';
import { openRightAside } from '@/components/results-drawer/results-drawer';
import { useI18n } from 'vue-i18n';
import type { CatalogItem, TechBgCardData } from '@/types';
import { useDirectory } from '@/composables/use-directory';
import { useDisposes } from '@/composables/use-disposes';
import { TechBgService } from '@/services/report/tech-bg-service';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { useReportViewSources } from '@/composables/use-report-view-sources';

import '@/assets/icons/techBg.svg';
import '@patsnap-ui/icon/assets/solid/chain.svg';

const { t } = useI18n()

const props = withDefaults(defineProps<{
    id: string;
    task: CatalogItem;
}>(), {
    id: '',
});

const patentTrend = ref<{ getBase64Str: () => string }>();
const paperTrend = ref<{ getBase64Str: () => string }>();
const isPatentChartLoaded = ref(false);
const isPaperChartLoaded = ref(false);

const { project } = useGlobalState();

const { updateDirectoryFinishStatus } = useDirectory()

const {
    charts,
    configTaskFunc,
    colorFunc,
} = useTechBgObjectives();

const { disposes } = useDisposes();

const isLoading = ref(false);

const isFailed = ref(false);

const cardInfo = ref<TechBgCardData>();

const groupKey = computed(() => props.task.task_group_type);

const disableRetry = computed(() => !project.value?.project_version);

const techBgService = injectService<TechBgService>(TechBgService);

const { handleViewSources } = useReportViewSources();

disposes.push(techBgService.onStatesChange(s => {
    cardInfo.value = s.data
    isLoading.value = s.isLoading
    isFailed.value = s.isFailed
}))

watch(() => props.id, (val) => {
    if (val) {
        techBgService.startFetch(val, groupKey.value);
    }
}, { immediate: true });

watch(() => isLoading.value, (isLoading) => {
    // 加载失败或者 加载成功，更新导出状态
    if (!isLoading || isFailed.value) {
        updateDirectoryFinishStatus(groupKey.value);
    }
})

watch(() => [isPatentChartLoaded.value, isPaperChartLoaded.value], ([patent, paper]) => {
    if (patent || paper) {
        updateDirectoryFinishStatus(props.task.task_group_type);
    }
})

function refreshCard() {
    techBgService.startFetch(props.id, groupKey.value, true);
}

async function getBase64Str() {
    const [patent, paper] = await Promise.all([
        patentTrend.value?.getBase64Str(),
        paperTrend.value?.getBase64Str()
    ])
    return {
        background_patent_trends: patent,
        background_paper_trends: paper,
    };
}

function handlePatentClick(data: {
    firstCode: string,
    measureCode: string,
    secondCode: string,
    thirdCode: string
}) {
    openRightAside({
        title: t('patent'),
        data: {
            project_id: props.id,
            chart_type: 'apd_y',
            dimension_list: [
                { field: 'APD_Y', value: data.firstCode }
            ],
        },
    });
}

function handlePaperClick(data: {
    firstCode: string,
    measureCode: string,
    secondCode: string,
    thirdCode: string
}) {
    openRightAside({
        title: t('paper'),
        data: {
            project_id: props.id,
            chart_type: 'paper_datetime_ts',
            dimension_list: [
                { field: 'datetime_ts', value: data.firstCode }
            ],
        },
    });
}

defineExpose({
    getBase64Str,
    refreshCard,
});

</script>
